<template>
  <div
    v-show="!hideInput"
    class="form-input"
    :class="[addMargin && 'mb-3',fullWidth && 'w-full']"
    v-bind="$attrs"
  >
    <Field
      v-bind="attrs"
      ref="formInputValidationProvider"
      v-slot="{ field, meta, errors }"
      :name="name"
      :rules="rules"
      :initial-value="initialValue"
    >
      <div class="flex items-center">
        <label
          v-if="!hideLabel"
          :for="name"
        >
          {{ label }} <span v-if="isRequired">*</span>
        </label>
        <span
          v-if="tooltip"
          v-tooltip="tooltipOptions"
          class="ml-2"
          :class="addMargin && 'mb-3'"
        >
          <i class="fas fa-question-circle cursor-pointer mb-2 text-sm" />
        </span>
        <span
          v-if="readOnly"
          class="ml-2 text-sm"
        >
          <i class="fas fa-lock cursor-pointer mb-2" />
        </span>
      </div>

      <slot />
      <component
        :is="isTextArea ? 'textarea' : 'input'"
        v-bind="field"
        :id="name"
        :type="!isTextArea ? 'text' : ''"
        :data-testid="name"
        :placeholder="placeholder"
        :disabled="readOnly"
        autocomplete="off"
        :class="{ disabled: readOnly }"
        :rows="rows"
        @blur="handleBlur($event)"
        @input="onInput($event)"
      />
      <button
        v-if="buttonTriggerUpdate && !loading"
        class="btn btn-primary btn-sm mt-3"
        @click.stop="emitUserUpdatePending"
      >
        Update
      </button>
      <span
        v-if="loading"
        class="spinner mt-3"
      />

      <span
        v-if="!hideErrors && errors.length"
        class="error-text"
      >
        {{ displayValidationError(errors) }}
      </span>
    </Field>
  </div>
</template>

<script>
import {Field} from 'vee-validate';
import debounce from "lodash/debounce";
import { tooltipBinding } from '~/utils/tooltipBinding';
import { isParticipantFacingRoute } from '~/utils/participantRoute';

export default {
  name: 'FormInput',
  components: {Field},
  inheritAttrs: false,
  props: {
    modelValue: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    rules: {
      type: [String, Object, Function],
      default: ''
    },
    initialValue: {
      default: null
    },
    validationMessage: {
      type: String,
      default: ''
    },
    hideInput: {
      type: Boolean,
      default: false
    },
    addMargin: {
      type: Boolean,
      default: true
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    hideLabel: {
      type: Boolean,
      default: false
    },
    hideErrors: {
      type: Boolean,
      default: false
    },
    isTextArea: {
      type: Boolean,
      default: false
    },
    buttonTriggerUpdate: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ""
    },
    name: {
      type: String,
      default: ''
    },
    tooltip: {
      type: String,
      default: ''
    },
    tooltipHtml: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: [String, Number],
      default: ''
    },
  },
  emits: ['update:modelValue', 'userUpdated'],
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    attrs() {
      return this.$attrs;
    },
    isRequired() {
      if (typeof this.rules === 'string') {
        return this.rules.includes('required');
      }

      if (typeof this.rules === 'object' && this.rules !== null) {
        return Boolean(this.rules.required);
      }

      return false;
    },
    tooltipOptions() {
      return tooltipBinding(this.tooltip, { html: this.tooltipHtml });
    },
  },
  mounted() {
    if(this.initialValue){
      this.applyInitial(this.initialValue);
    }
  },
  methods: {
    displayValidationError(errors) {
      if (this.validationMessage) {
        return this.validationMessage;
      }

      if (!errors.length) {
        return '';
      }

      if (isParticipantFacingRoute(this.$route) && this.isRequired) {
        return this.$t('General|fieldRequired');
      }

      return errors[0];
    },
    applyInitial(val) {
      if (this.$refs.formInputValidationProvider?.setValue) {
        this.$refs.formInputValidationProvider.setValue(val);
        this.emit(val);
      }
    },
    onInput(e) {
      this.emit(e.target.value);
    },
    handleBlur() {
      this.$emit('userUpdated');
    },
    setValueFromParent(newVal) {
      if (this.$refs.formInputValidationProvider?.setValue) {
        this.$refs.formInputValidationProvider.setValue(newVal);
      }
      this.emit(newVal);
    },
    clear() {
      if (this.$refs.formInputValidationProvider?.setValue) {
        this.$refs.formInputValidationProvider.setValue('');
      }
      this.emit('');
    },
    emit(newVal) {
      this.$emit('update:modelValue', {
        value: newVal,
        type: this.isTextArea ? 'textarea' : 'text',
        label: this.label
      });
    },
    emitUserUpdatePending() {
      this.loading = true;
      this.emitUserUpdate();
    },
    emitUserUpdate: debounce(function () {
      this.$nextTick(async () => {
        if (this.$refs.formInputValidationProvider) {
          const status = await this.$refs.formInputValidationProvider.validate();
          if (status.valid) {
            this.$emit('userUpdated');
            this.$events.fire('closeFloatingWindow');
          }
        }
        this.loading = false;
      });
    }, 1500),
  }
};
</script>

<style scoped lang="scss">
textarea {
  height: auto !important;
}
</style>
